import { Meta } from '@/types/legacyApi';
import { Dayjs } from 'dayjs';
import { ResponsiveStyleValue } from '@mui/system';
import { SideCategoryItem } from '@/components/SideCategory';
import { ApiLanguageCode } from '@/utils/language';
import { TalkFlowAISummarizeType } from '@/types/talk_flow';

export enum ApiTokenType {
  MS = 'MS',
  JWT = 'JWT',
}

export type BoardContent = {
  boardNo: string;
  documentNo: number;
  userName: string;
  deptName: string;
  title: string;
  createdDateTime: string;
  updatedDateTime: string;
  createdDate: string;
  updatedDate: string;
  content: string;
  readCount: number;
  commentCount: number;
  recommendCount: number;
  isMainBanner: number;
  imageUrls: string[];
  viewImage?: 'T' | 'B' | 'L' | 'S';
};

export type Board = {
  boardNo: string;
  documentNo: number;
  category: string;
  boardName: string;
  userName: string;
  deptName: string;
  title: string;
  createdDateTime: string;
  updatedDateTime: string;
  createdDate: string;
  updatedDate: string;
  readCount: number;
  commentCount: number;
  recommendCount: number;
  isMainBanner: number;
  dDay: string;
  thumbnailUrl: string;
  thumbnailPosition: string;
  contentPreview: string;
  watched: 0 | 1;
};

export type BoardLike = {
  boardNo: string;
  documentNo: number;
  userId: string;
  userName: string;
  readDateTime: string;
  readDate: string;
};

export type BoardLikes = {
  totalCount: number;
  details: BoardLike[];
};

export type User = {
  passportId: string;
  name: string;
  nickName: string;
  divisionName: string;
  departmentName: string;
  positionName: string;
  profileImageUrl: string | null;
};

export type UserV0 = {
  userId: string;
  email: string;
  name: string;
  nickName: string;
  placeName: string;
  deptName: string;
  positionName: string;
  mobilePhone: string;
  insidePhone: string;
  enterDate: string;
  companyName: string;
  userImageUrl: string;
  employeeNumber: string;
  organizationDisplayFullName: string;
  organizationDisplayShortName: string;
  birthDate: string;
  organizationDisplaySimpleName: string;
};

export type MyInfo = PassportDetail & {
  portalUserKey: string; // 포탈 계정 고유번호
  userId: string;
  passportId: string;
  enterDate: string;
  employmentType?: string; // 현재 고용구분 코드 (화면 미사용, BE 응답 대응용)
  employmentTypeName?: string; // 현재 고용상태 (임시직/계약직/정규직)
  employmentTypeDate?: string; // 현재 고용구분 발령일
  isFnCo: boolean;
  isFnHolding: boolean;
  language?: LanguageValue;
  job: {
    name: string;
    summary: string;
    content: string;
  };
  roles: string[]; // 보유 역할 key 목록 (ex. regular_employee)
  roleIds: string[]; // 보유 역할 id(ObjectId) 목록. 소셜 ACL role 스코프 매칭은 key 가 아닌 id 로 한다.
};

// 전자결재 타입 — types/approval.ts 에서 관리
export type {
  ApprovalState,
  WebApprovalState,
  ApprovalTempFilter,
  ApprovalFilter,
  PeopleFilterName,
  AllFilterName,
  StatusFilterName,
  FilterPassport,
  TotalApprovalParams,
  FilterStatus,
  PeriodStatus,
} from '@/types/approval';
export { ApprovalFilterType } from '@/types/approval';

/**
 * 임직원 본인 프로필에 반영되는 겸직 소속 정보.
 * HR 겸직 발령이 `detail.concurrentOrganizations` 배열로 내려오며,
 * 조직도는 각 원소의 organizationCode 조직에도 해당 임직원을 렌더링한다.
 * isLeader 는 본 소속과 별개로 해당 겸직 조직에서의 팀장 여부를 나타낸다.
 */
export type ConcurrentOrganization = {
  orderDate: string;
  departmentId: string;
  departmentCode: string;
  departmentName: string;
  divisionCode: string;
  divisionName: string;
  companyCode: string;
  companyName: string;
  organizationCode: string;
  organizationFullName: string;
  organizationShortName: string;
  isLeader: boolean;
  isPartLeader: boolean;
  isActingTeamLeader: boolean;
};

export type PassportDetail = {
  employeeNumber: string;
  name: string;
  nickname: string;
  email: string;
  positionName: string;
  positionCode?: string;
  monthsInPosition?: number;
  overallCareerMonths?: number;
  departmentName: string;
  departmentCode: string;
  departmentId: string;
  divisionName: string;
  divisionCode: string;
  companyCode: string;
  companyName: string;
  organizationCode: string;
  organizationFullName: string;
  organizationShortName: string;
  organizationDisplayName?: string;
  profileImageUrl: string;
  phoneInside: string;
  phoneMobile: string;
  birthdayType: number;
  birthday: string;
  joinedAt: string;
  isLeader: boolean;
  isPartLeader: boolean;
  isActingTeamLeader: boolean;
  workingHours: string;
  // 겸직이 없으면 빈 배열로 내려온다(null 아님).
  concurrentOrganizations?: ConcurrentOrganization[];
};

export type FnFComment = {
  boardNo: string;
  documentNo: number;
  commendNo: number;
  snsKey: number;
  recommendCount: number;
  recommended: 0 | 1;
  userId: string;
  userName: string;
  userNickName: string;
  userProfileUrl: string;
  deptName: string;
  content: string;
  deleted: number;
  createdDate: string;
  createdDateTime: string;
};

export type GetComments = {
  totalCount: number;
  details: FnFComment[];
};

export type MsLoginUrl = {
  msAzureLoginUrl: string;
};

export type MSLoginToken = {
  token_type: string;
  access_token: string;
  expires_at: number;
  refresh_token: string;
};

export type LinkFilter = {
  id: number;
  service: string;
  category: string;
  displayed: number;
  uri: string;
  name: string;
  description: string;
  icon: string;
  sortNo: number;
  createdUserId: string;
  updatedUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type LinkFilterList = LinkFilter[];

export type MsAzureToken = {
  id: string;
  mail: string;
  userId: string;
  displayName: string;
  givenName: string;
  jobTitle: string;
  mobilePhone: string;
  officeLocation: string;
  preferredLanguage: string;
  surname: string;
  userPrincipalName: string;
};

export type JwtToken = {
  tokenType: string;
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
};

export type JwtRefresh = {
  data: {
    token_type: string;
    access_token: string;
    expires_at: number;
  };
};

export type TokenInfo = {
  msAzure: MsAzureToken;
  jwt: JwtToken;
};

export type AzureRefreshToken = {
  data: {
    token_type: string;
    access_token: string;
    expires_at: number;
    refresh_token: string;
  };
};

export type MsUnreadEmailCount = {
  total: number;
};

export type IssueTokenParams = {
  grant_type: string;
  loginId: string;
  password: string;
  provider: string;
  access_token: string;
  id_token: string;
  refresh_token: string;
};
export type PortalIssueTokenParamsByPasswordToken = Pick<
  IssueTokenParams,
  'grant_type' | 'loginId' | 'password'
>;

export type PortalIssueTokenParamsByAuthToken = Pick<
  IssueTokenParams,
  'grant_type' | 'provider' | 'access_token'
>;

export type PortalIssueTokenParamsByRefreshToken = Pick<
  IssueTokenParams,
  'grant_type' | 'refresh_token'
>;

export type PortalLoginType =
  | PortalIssueTokenParamsByPasswordToken
  | PortalIssueTokenParamsByAuthToken
  | PortalIssueTokenParamsByRefreshToken;

export type PortalToken = {
  data: {
    token_type: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
    refresh_expires_in: number;
  };
};

export type EmpProofHistoryData = {
  cartNumber: number;
  businessDeptName: string;
  businessBumoonName: string;
  workStartDate: string;
  workEndDate: string;
  ouputReason: string;
  createDate: string;
  status: string;
};

export type CreateBoardData = {
  boardName: string;
  boardNo: string;
  category: string;
  subBoards?: CreateBoardData[];
};

export type SendMailData = {
  senderName: string;
  senderEmail: string;
  templateKey: string;
  receiverEmail: string;
  templateData: { name: string; message: string };
};

export type BookData = {
  bookId: string;
  title: string;
  isbn: string;
  author: string;
  category: { code: string; name: string };
  recommendCount: number;
  readCount: number;
  rentCount: number;
  ceoRecommend: string;
  imageUrl: string;
  createdAt: string;
};

export type NewBookData = {
  newBookCount: number;
  books: BookData[];
};

export type MyBookData = BookData & {
  rentalStartDate: string;
  rentalEndDate: string;
  rentalReturnDate: string;
};

export type ReadData = {
  boardNo: string;
  documentNo: string;
};

export type BookPeriod = {
  title?: string;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: number;
};

export type JiraAccountData = {
  jiraAccountId: string;
};

export type JiraIssue = {
  summary: string;
  statusCategory: {
    colorName: string;
    id: number;
    key: string;
    name: string;
    self: string;
  };
  statusName: string;
  issue: {
    id: string;
    key: string;
    url: string;
  };
  assignee: {
    avatarUrls: string;
    displayName: string;
    emailAddress: string;
    accountId: string;
  };
};

export type CreateCommentData = {
  boardNo: string;
  documentNo: string;
  content: string;
  isAnonymous: number;
};

export type NotiHistoryData = {
  id: number;
  data: {
    mobileActionType: string;
    tabName: string;
  };
  title: string;
  body: string;
  link: string;
  senderId: string;
  templateKey: string;
  createdAt: string;
  watched: 0 | 1;
};

export type NotiWithMeta = {
  data: NotiHistoryData[];
  meta: {
    totalCount: number;
    listSize: number;
    currentPage: number;
    endPage: number;
    unreadHistoryCount: number;
  };
};

export type NotiToken = {
  activated: number;
  agent: string;
  id: number;
  token: string;
  userId: string;
};

export type NotiTopic = {
  id: number;
  topic: string;
};

export type ProfileResponse = {
  profileUrl: string;
};

export type MenuData = {
  name: string;
  link?: string;
  action?: 'layer' | 'modal' | 'link' | 'newTab';
  gridNumber?: number;
  isDisplay: boolean;
  childData: MenuData[];
};

export type MenuResponse = {
  companyName: string;
  data: MenuData[];
};
export type EmployeeQuickSearch = {
  passportId: string;
  name: string;
  positionName: string;
  organizationShortName: string;
  departmentName: string;
  divisionName: string;
  profileImageUrl: string;
};

export type SearchResponse = {
  meta: Meta;
  data: Passport[];
};

export type Passport = {
  passportId: string;
  detail: PassportDetail;
  jobDescription: JobDescription;
  /** @deprecated 응답에서 생략될 수 있으며, 직책은 detail 값으로 판정한다. */
  displaySetting?: {
    showConcurrentPositionLabel: boolean;
    showTeamLeaderLabel: boolean;
    showPartLeaderLabel: boolean;
    showActingTeamLeaderLabel: boolean;
  };
  job: string;
  nodeId?: string;
  // 조직 관점에서는 겸직 소속 카드 여부, 인물 관점에서는 실제 겸직 소속 보유 여부.
  // 겸직 라벨은 이 값으로 판정하며 displaySetting은 사용하지 않는다.
  isConcurrent?: boolean;
};

export type BirthdayPassPort = {
  passport: Passport;
  isSendingEmail: boolean;
};

export interface IBirthdayObj {
  [day: string]: BirthdayPassPort[];
}

export const Language = {
  ko: '한국어',
  en: 'English',
  zh: '中文',
} as const;
type LanguageShape = typeof Language;
export type LanguageValue = keyof LanguageShape;

// UI에서 선택 가능한 언어 목록
export const SelectableLanguages: LanguageValue[] = ['ko', 'en', 'zh'];

export type PortalUserResponse = {
  id: string;
  loginId: string;
  personalization: {
    lang: LanguageValue;
  };
  defaultPassport: Passport;
  assignedPassports: Passport[];
  grantedRoles: {
    role: {
      id: string;
      key: string; // ex. regular_employee
      name: string; // ex. 정규직
      description: string; // ex. 정규직 (계약직 형태의 임원 포함)
    };
    createdBy: string;
    createdAt: string;
  }[];
};

export type UploadResponse = {
  uuid: string;
  fileName: string;
  fileHashName: string;
  downloadExpirationSeconds: number;
  fileSize: string;
  filePassword: string;
  createdAt: string;
  fileSizeInBytes: number;
};

export type DownLoadResponse = {
  filename: string;
  url: string;
  type: string;
  expiredAt: string;
};

export type Notification = {
  templateKey: string;
  serviceName: string;
  activated: boolean;
};

export type Dday = {
  id: string;
  calendarId: string;
  summary?: string;
  description?: string | null;
  start: Day;
  end: Day;
  meta: {
    dDay: boolean;
    location?: string;
    link?: string;
    linkTarget?: string;
    displayLeadDays?: number | null;
  };
};

export type Day = {
  allDay: boolean;
  datetime: string;
  timezone: string;
};

export type JobDescription = {
  summary: string | null;
  content: string | null;
  updatedAt: string | null;
};

export type Job = {
  id: number;
  name_kor: string;
  name_eng: string;
  name_chn: string;
  name_jpn: string;
  createdAt: string;
  updatedAt: string;
};

export type SearchJobList = {
  meta: Meta;
  data: Job[];
};

export type MyJob = {
  job: string;
  summary: string;
  content: string;
};

export type Organizations = {
  meta: Meta;
  data: Organization[];
};

export type Organization = {
  orgCode: string;
  parentOrgCode: string;
  parentName: string;
  name: string;
  nameShort: string;
  description: string;
  totalPassportCount: number;
  children: Organization[];
};

export type PassportSearchParams = {
  keyword?: string;
  listSize?: number;
  currentPage?: number;
  sortType?: string;
  orderType?: 'desc' | 'asc';
  activated?: 1 | 0 | '';
  orgCode?: string;
  sort?: string;
  emails?: string[];
  issuerName?: string;
};

export enum SortType {
  divisionName,
  name,
  positionCode,
  email,
  '',
}

export type {
  ApprovalParams,
  ApprovalUserBase,
  ApprovalAuthor,
  ApprovalApprover,
  ApprovalLastOpinionBy,
  Approval,
  ApprovalComment,
  ApprovalType,
  RecommendFilterResponse,
  ApprovalList,
} from '@/types/approval';

export type CategoryType = {
  id: string;
  name: string;
  description: string;
  groupId: string | null;
  group: string | null;
  groupSortNumber: number;
  // 그룹 생성자 passportId (레거시 nullable). 사이드바 케밥(이름변경/삭제) 노출 판단에 사용.
  groupCreatedBy?: string | null;
  // 사용자가 볼 수 있는 게시글 수. BE 집계에 실패하면 null.
  postCount?: number | null;
};

export type OverTimeHistory = {
  monthlyLimitHours: number;
  monthlyOvertimeHours: number;
  overtimes: any[];
  weeklyLimitHours: number;
  weeklyOvertimeHours: number;
};

export type TimeAdjustmentSummary = {
  totalOvertimeHours: number;
  usedHours: number;
  remainingHours: number;
  monthlyRequestCount: number;
  weeklyRequestCount: number;
  expiringOvertimes: OverTime[];
  activeOvertimes: OverTime[];
};

export type OverTime = {
  endedAt: string;
  overtimeId: number;
  remainingHours: number;
  startedAt: string;
  totalHours: number;
  usedHours: number;
  expiringAt: string;
};

export type BoardContentData = {
  id: string;
  subject: string;
  content: string;
  createdAt: string;
  thumbnails: Thumbnail[];
  displaySections: string[];
  commentCount: number;
  likeCount: number;
  recommendCount: number;
  isRead: boolean;
  isLike: boolean;
  isBookmark: boolean;
  isComment: boolean;
  isRecommend: boolean;
  isImportant: boolean;
  /** 목록 카드 제목 노출 여부. BE 미배포 환경에서는 오지 않을 수 있다. */
  isListSubjectVisible?: boolean;
  categories: string[];
  displayStartedAt: string;
  displayEndedAt: string | null;
  homeDisplayStartedAt: string;
  homeDisplayEndedAt: string | null;
  createdBy: User;
};

export type Thumbnail = {
  position: string;
  link: string;
};

export type BoardCreatedBy = {
  departmentName: string;
  divisionName: string;
  name: string;
  nickName: string | null;
  positionName: string;
};

export type BoardListItem = {
  categories: string[];
  commentCount: number;
  content: string;
  createdAt: string;
  createdBy: BoardCreatedBy;
  subject: string;
  id: string;
};

export type AncestorOrgCodes = Pick<
  Organization,
  'orgCode' | 'name' | 'description' | 'totalPassportCount'
>;

export type SalesData = {
  mainDate: Dayjs;
  mainSeason: string;
  rivalDate: Dayjs;
  analysisDate: Dayjs;
  analysisSeason: string;
};

export type Season = {
  season: string;
  sort: number;
};

/**
 * 시즌 기준 (PORTAL-13970) — 데이터 API 의 `seasonType` 값을 그대로 쓴다.
 *  - PRDT: 상품에 부여된 시즌으로 집계 (현행 동작·기본값)
 *  - OPER: 판매된 기간으로 집계. 사계절(N) 상품이 판매일에 따라 S/F 로 귀속된다
 */
export type SeasonBasis = 'PRDT' | 'OPER';

/**
 * GET /api/v2/sales/season 항목 — 두 기준의 목록이 함께 내려온다 (PORTAL-14097).
 * 실제 응답 필드는 `seasonType`(OPER | PRDT). 티켓·데이터 API 문서는 `sesnType` 으로 적혀 있어 둘 다 받는다.
 */
export type SeasonV2 = Season & {
  seasonType?: string;
  sesnType?: string;
};

export type SalesMainParams = {
  season: string;
  date: string;
  /** 미지정·PRDT 면 쿼리에 붙이지 않는다 — 기존 SWR 키·BE 기본 동작을 그대로 유지 */
  seasonBasis?: SeasonBasis;
};

export type Period = 'M' | 'D';

/** 전년비 비교 기준일 — 일매출(D)은 시작일과 종료일이 같다. 주간(W)·연누적(Y)도 함께 온다 (PORTAL-14709) */
export type SalesCompareDate = {
  period: Period | 'W' | 'Y';
  targetDate: string;
  previousStartDate: string;
  previousEndDate: string;
};

export type SalesBrandStoresParams = {
  brand: string;
  period: Period;
} & SalesMainParams;

export type StoreListParams = {
  channelName?: string[];
  regionName?: string[];
  gradeName?: string[];
  shopStatus?: string[];
} & SalesBrandStoresParams;

export type GlobalAmt = {
  curr_amt: number;
  prev_amt: number;
  display_curr_amt: string;
  display_prev_amt: string;
  rate: number;
  currency: string;
};

export type BrandData = {
  brandCode: string;
  brandName: string;
  bizType: string;
  period: string;
  typeZone: string;
  currentAmount: number;
  previousAmount: number;
};

export type BrandDetail = {
  brand_code: string;
  title: string;
  d_link_shop_list: string;
  d_link_tr_type_list: string;
  d_link_sale_analytics: string[];
  d_total_curr_amt: number;
  d_total_prev_amt: number;
  d_total_display_curr_amt: string;
  d_total_display_prev_amt: string;
  d_total_rate: number;
  d_curr_amt: number;
  d_prev_amt: number;
  d_display_curr_amt: string;
  d_display_prev_amt: string;
  d_rate: number;
  d_dutyfree_curr_amt: number;
  d_dutyfree_prev_amt: number;
  d_dutyfree_display_curr_amt: string;
  d_dutyfree_display_prev_amt: string;
  d_dutyfree_rate: number;
  d_global_total: GlobalAmt[];
  d_global: GlobalAmt[];
  d_global_dutyfree: GlobalAmt[];
  m_link_shop_list: string;
  m_link_tr_type_list: string;
  m_link_sale_analytics: string[];
  m_total_curr_amt: number;
  m_total_prev_amt: number;
  m_total_display_curr_amt: string;
  m_total_display_prev_amt: string;
  m_rate: number;
  m_global_total: GlobalAmt[];
  m_global: GlobalAmt[];
  m_global_dutyfree: GlobalAmt[];
  m_curr_amt: number;
  m_prev_amt: number;
  m_display_curr_amt: string;
  m_display_prev_amt: string;
  m_total_rate: number;
  m_dutyfree_curr_amt: number;
  m_dutyfree_prev_amt: number;
  m_dutyfree_display_curr_amt: string;
  m_dutyfree_display_prev_amt: string;
  m_dutyfree_rate: number;
};

export type SalesMain = {
  result: BrandData[];
  display_rows: BrandDetail[];
  updated_at: string;
};

export type BrandSales = {
  brandCode: string;
  brandName: string;
  bizType: string;
  currencies: CurrencyData[];
  flagUrl: string;
  logoUrl: string;
};

export type CurrencyData = {
  currencyUnit: Unit;
  daily: PeriodSales;
  monthly: PeriodSales;
};

export type PeriodSales = {
  bizType: string;
  brandCode: string;
  brandName: string;
  currExRateStandardDate: string;
  currencyUnit: Unit;
  currentAmount: number;
  currentAmountGlobal: number;
  currentExchangeRate: string;
  local: LocalSales;
  dutyfree: SalesBase | null;
  localSaleRatio: string;
  period: string;
  prevExRateStandardDate: string;
  previousAmount: number;
  previousAmountGlobal: number;
  previousExchangeRate: string;
  recordTime: string;
  saleRatio: string;
  typeZone: string;
};

export type Unit =
  | 'KRW'
  | 'USD'
  | 'CNY'
  | 'JPY'
  | 'EUR'
  | 'HKD'
  | 'NTD'
  | 'MOP';

export type LocalSales = {
  bizType: string;
  brandCode: string;
  brandName: string;
  currencyUnit: Unit;
  currentAmount: number;
  currentAmountGlobal: number;
  localSaleRatio: string;
  currentExchangeRate: string;
  online: SalesBase;
  offline: SalesBase;
  period: string;
  previousAmount: number;
  previousAmountGlobal: number;
  previousExchangeRate: string;
  recordTime: string;
  saleRatio: string;
  typeZone: string;
};

export type SalesBase = {
  recordTime: string; // "2025-12-23 05:09:41"
  brandCode: string; // "X", "M", ...
  brandName: string;
  bizType: string; // "A"
  period: 'D' | 'M'; // Daily / Monthly
  typeZone: 'ALL' | 'A' | 'B' | 'ON' | 'OFF';
  currentAmount: number;
  previousAmount: number;
  currentAmountGlobal: number;
  previousAmountGlobal: number;
  currencyUnit: Unit; // KRW, CNY, HKD, NTD, ...
  currentExchangeRate: string; // "1.00"
  previousExchangeRate: string; // "1.00"
  saleRatio: string; // "81.700"
  localSaleRatio: string; // "81.700"
};

export type ShopData = {
  shopCode: string;
  shopName: string;
  currentAmount: string;
  previousAmount: string;
  period: string;
  openDate: string;
  brandTypeYN: string;
  customUseYN: string;
};

export type NewShopDetailData = {
  brandTypeYN: string;
  currencyUnit: string;
  currentAmount: number;
  currentExchangeRate: string;
  currentLocalAmount: number;
  customUseYN: string;
  localSaleRatio: string;
  openDate: string;
  period: string;
  previousAmount: number;
  previousExchangeRate: string;
  previousLocalAmount: number;
  saleRatio: string;
  shopCode: string;
  shopName: string;
};

export type ShopDetailData = {
  brand_code: string;
  curr_amt: number;
  cust_useyn: string;
  display_curr_amt: string;
  display_prev_amt: string;
  dmflag: string;
  link_tr_type_list: string;
  local_currency: string;
  local_current_amount: string;
  local_display_current_amount: string;
  local_display_previous_amount: string;
  local_previous_amount: string;
  local_rate: 0;
  opendate: string;
  prev_amt: 0;
  rate: string;
  shop_code: string;
  shopinfo_yn: string;
  title: string;
};
export type BrandStore = {
  result: ShopData[];
  display_rows: ShopDetailData[];
  display_unit: string;
};

export type StoreFilter = {
  brandCode: string;
  channelName: string[];
  regionName: string[];
  gradeName: string[];
  shopStatus: string[];
};

export type SalesTypeResponse = {
  saleType: string;
  sort: string;
  saleAmount: number;
  totalFlag: string;
  numberFlag: string;
  currencyUnit: Unit;
};

export type BestItemType = {
  brandCode: string;
  season: string;
  productCode: string;
  productName: string;
  colorCode: string;
  sort: number;
  saleQuantity: number;
  title: string;
  subTitle: string;
  image: string;
  price: number;
};

export type Pagination = {
  currentPage: number;
  lastPage: number;
  size: number;
  totalCount: number;
};

export type BestItemTypeV2 = {
  brandCode: string;
  image: string;
  currencyUnit: Unit;
  price: number;
  productCode: string;
  productName: string;
  saleQuantity: number;
  season: string;
  sort: number;
  subTitle: string;
  title: string;
};

export type ProductKindCode = 'L' | 'A';

export type ItemDetailParams = {
  season: string;
  brand: string;
  productCode?: string;
  shopCode: string;
};

export type SalesDetail = {
  brandCode: string;
  colorCode: string;
  productCode: string;
  productName: string;
  quantity: number;
  season: string;
  url: string;
};

export type SalesByStoreRanking = {
  season: string;
  brand: string;
  productCode?: string;
  colorCode?: string;
};

export type ItemSalesByStoreRanking = {
  brandCode: string;
  productCode: string;
  quantity: number;
  season: string;
  shopCode: string;
  shopName: string;
};

export type RivalRankResponse = {
  brandCode: string;
  brandName: string;
  sort: string;
  currentRank: string;
  previousRank: string;
  upDown: string;
};

export type RivalCompanyStandardResponse = {
  rivalCode: string;
  rivalName: string;
  sort: string;
  rivalSaleAmount: string;
  saleAmount: string;
  ratio: string;
};

export type RivalStoreResponse = {
  brandCode: string;
  shopCode: string;
  shopName: string;
  currentRank: string;
  previousRank: string;
  upDown: string;
};

export type AnalysisData = {
  brandCode: string;
  brandName: string;
  season: string;
  sort: string;
  unLink: string;
  saleAmountDay: number;
  saleAmountExiDay: number;
  saleAmountBeforeDay: number;
  saleAmountExiBeforeDay: number;
  saleAmountWeek: number;
  saleAmountExiWeek: number;
  saleAmountBeforeWeek: number;
  saleAmountExiBeforeWeek: number;
  saleAmountMonth: number;
  saleAmountExiMonth: number;
  saleAmountBeforeMonth: number;
  saleAmountExiBeforeMonth: number;
  saleAmountYear: number;
  saleAmountExiYear: number;
  saleAmountBeforeYear: number;
  saleAmountExiBeforeYear: number;
  saleRatioDay: number;
  saleRatioWeek: number;
  saleRatioMonth: number;
  saleRatioYear: number;
  saleRatioExiDay: number;
  saleRatioExiWeek: number;
  saleRatioExiMonth: number;
  saleRatioExiYear: number;
};

export type ChannelInfo = {
  groupId: string;
  totalType: string;
  shopTypeCode: string;
  shopTypeName: string;
  shopDetailTypeCode: string;
  shopDetailTypeName: string;
  locationCode: string;
  locationName: string;
  shopCount: number;
  saleAmountDay: number;
  saleRatioDay: string;
  saleAmountWeek: number;
  saleRatioWeek: string;
  saleAmountMonth: number;
  saleRatioMonth: string;
  saleGoalMonth: number;
  saleGoalRatioMonth: string;
  saleAmountYear: number;
  saleRatioYear: string;
};

export type CustomSalesDetail = {
  shopDetailTypeName: string;
  locationName: string;
  shopCount: number;
  amount: number;
  ratio: string;
  saleGoalRatioMonth?: string;
};

export type SeasonInfo = {
  season: string;
  saleAmountDay: number;
  saleRatioDay: string;
  saleAmountWeek: number;
  saleRatioWeek: string;
  saleGoalMonth: string;
  saleAmountMonth: number;
  saleGoalRatioMonth: string;
  saleRatioMonth: string;
  saleAmountYear: number;
  saleRatioYear: string;
};

export type ItemInfo = {
  season: string;
  sortSeason: string;
  productKindName: string;
  productKindDetailName: string;
  sortProductKind: string;
  sortProductKindDetail: string;
  saleAmountDay: number;
  saleRatioDay: string;
  saleRatioDayTotal: string;
  saleRatioBeforeDayTotal: string;
  saleAmountWeek: number;
  saleRatioWeek: string;
  saleRatioWeekTotal: string;
  saleRatioBeforeWeekTotal: string;
  saleAmountMonth: number;
  saleRatioMonth: string;
  saleRatioMonthTotal: string;
  saleRatioBeforeMonthTotal: string;
};

export type CalendarId = {
  id: string;
  summary: string;
  description: string;
  public: boolean;
};

export type ItemParams = {
  season: string;
  brand: string;
  productCode?: string;
  date?: string;
};

export type SaleByColorData = {
  brandCode: string;
  season: string;
  productCode: string;
  productName: string;
  colorCode: string;
  url: string;
  firstSaleDate: string;
  firstDeliveryDate: string;
  storeQuantity: number;
  saleRate: string;
  deliveryQuantity: number;
  saleQuantity: number;
  waitDeliveryQuantity: number;
  stockQuantity: number;
  wslDeliveryQuantity: number;
  saleQuantityWeek: number;
  wos: string;
};

export type ItemImageList = {
  brandCode: string;
  season: string;
  productCode: string;
  colorCode: string;
  seq: number;
  url: string;
  imageFrom: string;
  defaultYN: string;
  updateDate: string;
};
export type ItemDetail = {
  brandCode: string;
  season: string;
  productCode: string;
  productName: string;
  url: string;
  firstSaleDate: string;
  storeQuantity: number;
  saleRate: string;
  saleQuantity: number;
  stockQuantity: number;
  saleQuantityWeek: number;
  wos: string;
};

export type ItemDetailRow = {
  id: string;
  title: string;
  value: string;
};

export type ShopInfoParams = {
  season: string;
  brand: string;
  shopCode: string;
  date: string;
  period: string;
};

export type ShopInformationVO = {
  brandCode: string;
  shopCode: string;
  shopName: string;
  shopManagerCode: string;
  shopManagerName: string;
  phone: string;
  address: string;
  openDate: string;
  latitude: string;
  longitude: string;
  bizCode: string;
  locationCode: string;
  locationName: string;
  localBrand: string;
};

export type ShopCurrentSalesVO = {
  achieveRate: string;
  currencyUnit: Unit;
  custSaleAmount: number;
  custSaleRate: string;
  saleAmount: number;
  saleAmountBefore: number;
  saleGoalAmount: number;
  saleLocalAmount: number;
  saleLocalAmountBefore: number;
};

export type ShopSalesVOList = {
  flag: string;
  currencyUnit: string;
  totalSaleBefore2Year: string;
  totalSaleBefore1Year: string;
  totalSaleCurrentYear: string;
  totalSaleBefore2YearFromHalf: string;
  totalSaleBefore1YearFromHalf: string;
  totalSaleCurrentYearFromHalf: string;
  totalLocalSaleBefore2Year: string;
  totalLocalSaleBefore1Year: string;
  totalLocalSaleCurrentYear: string;
  totalLocalSaleBefore2YearFromHalf: string;
  totalLocalSaleBefore1YearFromHalf: string;
  totalLocalSaleCurrentYearFromHalf: string;
};
export type ShopSalesPatternVOList = {
  flag: string;
  year: string;
  month: string;
  saleAmount: number;
};
export type ShopSalesRatioVOList = {
  sort: string;
  month: string;
  shopSaleRatio: string;
  saleRatio: string;
  brandSaleRatio: string;
};
export type ShopRivalRankVO = {
  currentMonthRank: string;
  previousMonthRank: string;
  previousYearRank: string;
};

export type ShopInfo = {
  shopInformationVO: ShopInformationVO;
  shopCurrentSalesVO: ShopCurrentSalesVO;
  shopRivalRankVO: ShopRivalRankVO;
  shopSalesVOList: ShopSalesVOList[];
  shopSalesPatternVOList: ShopSalesPatternVOList[];
  shopSalesRatioVOList: ShopSalesRatioVOList[];
};

export type Weather = {
  id: string;
  calendarId: string;
  summary: string;
  description: string;
  start: Time;
  end: Time;
  meta: {
    dDay: boolean;
    lowTemperature: number;
    highTemperature: number;
  };
};

export type Time = {
  allDay: boolean;
  datetime: string;
  timezone: string;
};

export type VersionInfo = {
  isTestFlight: boolean;
  isNeeded: boolean;
  currentVersion: string;
  latestVersion: string;
  storeUrl: string;
};

export type AppConfig = {
  alarmNotice: boolean;
  dayFind: boolean;
  language: string;
  seasonFind: boolean;
  server: string;
};

export type RNAuthInfo = {
  msUserId: string;
  portalApiId: string;
  portalApiKey: string;
  refreshToken: string;
  msAccessToken: string;
  msRefreshToken: string;
};

export type NativeMessage<T> = {
  type: string;
  data: T;
};

export type OpenState = {
  main: OpenParams;
  shopCode: string;
};

export type OpenParams = {
  mainIndex: number;
  subIndex: number;
};

export type LeaveDuration = {
  day: number;
  hour: number;
  minute: number;
};

/**
 * 휴가 취소 가능 유형 (leave/history 응답의 cancelType)
 * - immediate : 사용 시작일이 오늘 이후(당일 포함) → 결재 없이 즉시 취소
 * - approval  : 사용 시작일이 지났으나 당해 연도 품의(정산 전) → 취소 신청 품의 필요
 * - none      : 당해 연도 외 품의(정산 이후) → 취소 불가
 */
export type LeaveCancelType = 'immediate' | 'approval' | 'none';

export type VacationStatus = {
  annualLeave: Leave;
  monthlyLeave: Leave;
  overage: number;
  overageDuration?: LeaveDuration;
};

export type Leave = {
  granted: number;
  grantedDuration?: LeaveDuration;
  used: number;
  usedDuration?: LeaveDuration;
};

export type SocialState = {
  categoryData: SideCategoryItem[];
  isReserve: boolean;
  isBookmark: boolean;
  categoryId: string;
  groupId: string;
  currentDate: string;
  isTopicRender: boolean;
};

export type SocialSidebarCategory = {
  id: string;
  name: string;
  groupId: string | null;
  postCount?: number | null;
};

export type SocialSidebarGroup = {
  id: string;
  name: string;
  // 그룹 생성자 passportId (레거시 nullable). 케밥 메뉴 노출 판단에 사용.
  createdBy?: string | null;
  sortNumber?: number | null;
  categories: SocialSidebarCategory[];
};

export type SocialLayoutState = {
  isRender: boolean;
  columns: number;
  isExpanded: boolean;
  stickyFlexDirection: ResponsiveStyleValue<'row' | 'column'>;
};

export type OrgChartState = {
  searchText: string;
  searchPassports: Passport[];
  targetSearchIndex: number;
};

export type OrgChartStateGlobal = {
  memberOpen: boolean;
  isOnlySelectedView: boolean;
};

export type NotificationData = {
  title: string;
  body: string;
  link: string;
  messageId: string;
};

export type NotificationState = NotificationData[];

export type NlResponse = {
  id: string;
  object: string;
  createdAt: string;
  assistantId: string;
  threadId: string;
  status: string;
};

export type GptResponse = {
  id: string;
  object: string;
  createdAt: string;
  assistantId: string;
  threadId: string;
  runId: string;
  role: string;
  content: [
    {
      type: string;
      text: {
        value: string;
        annotations: [];
      };
    },
  ];
  attachments: [];
  metadata: [];
};

export type GptEmployee = {
  id: string;
  name: string;
  companyName: string;
  divisionName: string;
  departmentName: string;
  positionName: string;
  jobDescription: string;
  profileImageUrl: string;
};

export type GptEmployeeResponse = {
  headerMessage: string;
  footerMessage: string;
  results: GptEmployee[];
};

export type {
  FavoriteFilter,
  FavoriteApprovalFilter,
  FavoriteList,
  FavoriteFilterData,
} from '@/types/approval';

// Document (Talk Flow) Favorite Filter Types
export type DocumentFavoriteList = Pick<
  DocumentListFilters,
  | 'createdBy'
  | 'internalParticipants'
  | 'documentTypes'
  | 'keyword'
  | 'searchFields'
  | 'filterName'
>;

export type DocumentFavoriteFilter = {
  name: string;
  description: string;
  params: DocumentFavoriteList;
};

export type SearchData = {
  keyword: string;
  searchFields: SearchFields;
};

export type SearchFields = 'subject,content' | 'subject' | 'content';

export enum FAQ_STATUS {
  loading,
  done,
  canceled,
  error,
}

export type Congestion = {
  congsBaseInfoList: [
    {
      congsSeatCnt: number;
      notiItmCtnt: null;
      notiItmUseYn: string;
      outsCongsDt: string;
      congsVaddFunc: string;
      bizplcCd: string;
      timeTable: string;
      opertYn: string;
      bizplcNm: string;
      congsMethod: '01' | '02' | '03' | '04';
      ciUseYn: string;
    },
  ];
  congsInfoList: [
    {
      congsSeatCnt: number;
      congsZoneCd: '01' | '02' | '03' | '04';
      bizCalcCnt: number;
      congsStbyTm: string;
      congsVaddFunc: string;
      mealFgNm: string;
      congsMethod: string;
    },
  ];
  errorCode: number;
  errorMsg: string;
};

export type CongestionStatus = {
  title: string;
  contentColor: string;
  backgroundColor: string;
  svgImageName: string;
  subTitle: string;
};

export enum ShareMode {
  Share,
  Comment,
  Delete,
  Owner,
}

export enum RecordMode {
  NEW,
  Note,
  Meeting,
}
export const DocumentTypeByRecordMode: { [key in RecordMode]: DocumentType } = {
  [RecordMode.NEW]: 'new',
  [RecordMode.Note]: 'note',
  [RecordMode.Meeting]: 'meeting',
};
export type RecordAudio = {
  data: File;
  duration?: string;
};

export type PassportKey = 'internal' | 'shareAttender';

export enum DataMode {
  Approval,
  Meeting,
}
export type DataType = {
  mode: DataMode;
  id: string;
  title: string;
  link: string;
};

export type ScheduleEvent = {
  id: string;
  subject: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
};

export type OutLookSchedule = {
  displayRange: {
    start: string;
    end: string;
    formatted: string;
  };
  events: ScheduleEvent[];
};

export type OutlookScheduleDetail = {
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  changeKey: string;
  categories: string[];
  transactionId: string | null;
  originalStartTimeZone: string;
  originalEndTimeZone: string;
  iCalUId: string;
  uid: string;
  reminderMinutesBeforeStart: number;
  isReminderOn: boolean;
  hasAttachments: boolean;
  subject: string;
  bodyPreview: string;
  importance: 'low' | 'normal' | 'high';
  sensitivity: 'normal' | 'personal' | 'private' | 'confidential';
  isAllDay: boolean;
  isCancelled: boolean;
  isOrganizer: boolean;
  responseRequested: boolean;
  seriesMasterId: string | null;
  showAs:
    | 'free'
    | 'tentative'
    | 'busy'
    | 'oof'
    | 'workingElsewhere'
    | 'unknown';
  type: 'singleInstance' | 'occurrence' | 'exception' | 'seriesMaster';
  webLink: string;
  onlineMeetingUrl: string;
  isOnlineMeeting: boolean;
  onlineMeetingProvider:
    | 'teamsForBusiness'
    | 'skypeForBusiness'
    | 'skypeForConsumer'
    | 'unknown';
  allowNewTimeProposals: boolean;
  occurrenceId: string | null;
  isDraft: boolean;
  hideAttendees: boolean;
  responseStatus: {
    response:
      | 'none'
      | 'organizer'
      | 'tentativelyAccepted'
      | 'accepted'
      | 'declined'
      | 'notResponded';
    time: string;
  };
  body: {
    contentType: 'html' | 'text';
    content: string;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location: {
    displayName: string;
    locationUri: string;
    locationType:
      | 'default'
      | 'conferenceRoom'
      | 'homeAddress'
      | 'businessAddress'
      | 'geoCoordinates'
      | 'streetAddress'
      | 'hotel'
      | 'restaurant'
      | 'localBusiness'
      | 'postalAddress';
    uniqueId: string;
    uniqueIdType: 'unknown' | 'locationStore' | 'directory';
    address: {
      street: string;
      city: string;
      state: string;
      countryOrRegion: string;
      postalCode: string;
    };
    coordinates: any[]; // 구조가 없으니 any[]로
  };
  locations: OutlookScheduleDetail['location'][];
  recurrence?: {
    pattern: {
      type:
        | 'daily'
        | 'weekly'
        | 'absoluteMonthly'
        | 'relativeMonthly'
        | 'absoluteYearly'
        | 'relativeYearly';
      interval: number;
      month: number;
      dayOfMonth: number;
      daysOfWeek?: string[];
      firstDayOfWeek?: string;
      index?: 'first' | 'second' | 'third' | 'fourth' | 'last';
    };
    range: {
      type: 'endDate' | 'noEnd' | 'numbered';
      startDate: string;
      endDate?: string;
      recurrenceTimeZone?: string;
      numberOfOccurrences?: number;
    };
  };
  attendees: {
    type: 'required' | 'optional' | 'resource';
    status: {
      response:
        | 'none'
        | 'organizer'
        | 'tentativelyAccepted'
        | 'accepted'
        | 'declined'
        | 'notResponded';
      time: string;
    };
    emailAddress: {
      name: string;
      address: string;
    };
  }[];
  organizer: {
    emailAddress: {
      name: string;
      address: string;
    };
  };
  onlineMeeting: any; // null or future shape if needed
};

export type SummarizeParams = {
  content?: string;
  recordingFiles: { uuid: string }[];
  prompt: string;
  attendeeNames: string[];
  summarizeType: TalkFlowAISummarizeType;
};

export type RecordAiState = {
  abortController: AbortController | null;
};

export type Author = {
  passportId: string;
  name: string;
  departmentName: string;
  positionName: string;
  profileImageUrl: string;
  placeName: string;
};

export type InternalParticipant = {
  passportId: string;
  name: string;
  departmentName: string;
  positionName: string;
  divisionName: string;
  profileImageUrl: string;
};

export type RecentShareScope = {
  type: 'passport' | 'role';
  value: string;
};

export type RecentShareRole = {
  id: string;
  key: string;
  name: string;
  type: 'companyRole' | 'divisionRole' | 'departmentRole' | null;
};

export type RecentSharePassportTarget = {
  scope: { type: 'passport'; value: string };
  detail: Passport;
};

export type RecentShareRoleTarget = {
  scope: { type: 'role'; value: string };
  detail: RecentShareRole;
};

export type RecentShareTarget =
  | RecentSharePassportTarget
  | RecentShareRoleTarget;

export const isRecentSharePassportTarget = (
  target: RecentShareTarget,
): target is RecentSharePassportTarget => target.scope.type === 'passport';

export type Attachment = {
  name: string;
  uuid: string;
  size: string;
};

export type Relation = {
  name: string;
  link: string;
  type: string;
  id: string;
};

export type Recording = {
  name: string;
  uuid: string;
  duration?: string; // "HH:mm:ss" or "mm:ss"
  state: 'uploading' | 'complete' | 'error';
};

export type RecordListType = {
  id: string;
  subject: string;
  suggestedSubject?: string;
  meetingMsSubject?: string; // MS 캘린더 원본 미팅 이름 (회의록 리스트 표시용)
  meetingDate?: string; // "YYYY-MM-DD HH:mm:ss"
  meetingType: string;
  isTemporary: boolean;
  createdBy: InternalParticipant;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  documentType: DocumentType;
  // @deprecated: [/PORTAL-9122]
  // summarizedStatus: string;
  documentSummarizedStatus: string;
  commentCount: number;
  isBookmarked: boolean;
  isRead: boolean;
  participants: {
    internal: InternalParticipant[];
    // 생성·수정 API 가 external 생략을 허용해 키 없이 저장된 문서는 null 로 내려온다
    external: string[] | null; // 외부인 이름 배열
  };
  aclCount: number;
  latestComment: {
    id: string;
    commentGroupId: string;
    content: string;
    createdBy: {
      passportId: string;
      activated: number;
      job: string;
      jobDescription: {
        summary: string;
        content: string;
        updatedAt: string; // e.g., "2025-09-16 14:20:45"
      };
      detail: {
        employeeNumber: string;
        name: string;
        nickname: string;
        email: string;
        positionName: string;
        departmentId: string;
        departmentCode: string;
        departmentName: string;
        divisionCode: string;
        divisionName: string;
        companyCode: string;
        companyName: string;
        organizationCode: string;
        organizationFullName: string;
        organizationShortName: string;
        isLeader: boolean;
        profileImageUrl: string;
        phoneInside: string;
        phoneMobile: string;
        birthdayType: number;
        birthday: string; // "MM-DD"
        joinedAt: string; // "YYYY-MM-DD"
      };
      /** @deprecated 응답에서 생략될 수 있으며, 직책은 detail 값으로 판정한다. */
      displaySetting?: {
        showTeamLeaderLabel: boolean;
        showConcurrentPositionLabel: boolean;
        showPartLeaderLabel: boolean;
        showActingTeamLeaderLabel: boolean;
      };
    };
    createdAt: string; // ISO format: "2025-07-17T02:08:06.166000Z"
    updatedAt: string;
  };
};

export type RecordDetail = {
  id: string;
  subject: string;
  content: string;
  memo: string;
  commentGroupId: string;
  participants: {
    internal: InternalParticipant[];
    external: string[] | null; // 외부인 이름 배열
  };
  attachments: Attachment[];
  relations: Relation[];
  recordings: Recording[];
  meetingDate: string; // "YYYY-MM-DD HH:mm:ss"
  meetingType: string;
  isTemporary: boolean;
  createdBy: InternalParticipant;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  meetingMsSubject: string;
  meetingMsLink: string;
  meetingMsId: string;
  documentType: DocumentType;
  isBookmarked: boolean;
  aiPipelineIds?: string[];
  suggestedSubject?: string;
  commonSummary?: CommonSummary | null;
};

// 회의록 공통 요약본 (AI 요약) — 문서 GET 응답에 포함, /summarize 완료 시 채워짐
export type CommonSummary = {
  text: string;
  generatedAt: string; // ISO8601 (+09:00)
};

// ── AI 채팅 (PORTAL-11968) ──────────────────────────────
// 답변 근거 소스 범위
export type TalkFlowChatSourceScope =
  | 'recording' // 음성파일(STT 전사문)
  | 'content' // 본문(AI 정리본 + 회의 메모)
  | 'recording_and_content'; // 둘 다

// POST .../chat 요청
export type TalkFlowChatSendRequest = {
  message: string;
  sourceScope: TalkFlowChatSourceScope;
  sessionId: string | null; // 없으면 새 세션
};

// POST .../chat 응답 (202)
export type TalkFlowChatSendResponse = {
  sessionId: string; // 영속 세션(이력 조회 키)
  threadId: string; // 이번 요청 스레드
  topic: string; // 실제 구독 topic — talk-flow-chat:{threadId} (sessionId 아님!)
  aiPipelineId: string; // 이번 질문 1건 식별자
};

// 세션 목록 item (최신순). 세션 식별자 필드는 `id` (sessionId 아님)
export type TalkFlowChatSession = {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt?: string;
};

export type TalkFlowChatMessageType = 'text' | 'source_changed';
export type TalkFlowChatRole = 'user' | 'assistant' | null;

// GET .../chat/sessions/{sessionId}/messages item
export type TalkFlowChatMessage = {
  id: string;
  type: TalkFlowChatMessageType;
  role: TalkFlowChatRole; // source_changed 는 null
  content: string | null; // source_changed 는 null
  sourceScope?: TalkFlowChatSourceScope; // 사용자 메시지에만
  createdAt: string;
};

// SSE payload — topic talk-flow-chat:{sessionId}
export type TalkFlowChatAnswerCompletePayload = {
  event: 'talk_flow.chat.answer.complete';
  aiPipelineId: string;
  talkFlowDocumentId: string;
  sessionId: string;
  answer: string;
};

export type TalkFlowChatAnswerErrorPayload = {
  event: 'talk_flow.chat.answer.error';
  aiPipelineId: string;
  talkFlowDocumentId: string;
  sessionId: string;
  status: string;
};

export type TalkFlowConfig = {
  meetingDate: string; // "YYYY-MM-DD HH:mm:ss"
  meetingType: string;
  meetingTypeId: string;
  isTemporary: boolean;
  meetingMsLink: string;
  meetingMsSubject: string;
  meetingMsId: string;
  documentType: DocumentType;
};

export type RecordFilter = {
  docId: string;
  mode: RecordMode;
  subject: string;
  meetingMsSubject: string;
  meetingMsLink: string;
  meetingMsId: string;
  meetingDate: Dayjs | null;
  meetingType: string;
  meetingTypeId: string;
  internal: InternalParticipant[];
  external: string[];
  recordings: Recording[];
  attachments: Attachment[];
  content: string;
  relations: Relation[];
  searchFields: {
    innerAttender: string;
    externalAttender: string;
    shareAttender: string;
    dataSearch: string;
  };
  shareAttender: InternalParticipant[];
  createdBy: InternalParticipant | null;
  createAt: string;
  commentGroupId: string;
  isBookmarked: boolean;
  memo: string; // 메모 본문 HTML (AI 정리와 별개)
};

export type CreateTalkFlowParams = Pick<
  RecordDetail,
  'subject' | 'aiPipelineIds'
> & {
  content: string; // 회의록 내용 HTML string
  memo: string; // 메모 본문 HTML string
  participant: {
    internal: string[]; // passportId[]
    external: string[]; // 외부 참가자 이름 배열
  };
  attachments: Attachment[];
  relations: Relation[];
  config: TalkFlowConfig;
};

export type Translation = {
  contents: string[];
  targetLanguage: ApiLanguageCode;
};

export type TalkFlowShare = {
  id: string;
  role: string;
  scope: {
    type: string;
    value: string;
  };
  detailInfo: InternalParticipant;
};

export type ShareModeParam = {
  aclData: {
    role: string;
    scope: {
      type: string;
      value: string;
    };
  }[];
};

export type RecordDataState = {
  openDraggable: boolean;
  docId: string;
  title: string;
  isUploading: boolean;
  /** 녹음 파일 업로드 진행률 0~100 (PORTAL-12593) */
  uploadProgress: number;
  recordingData: Recording | null;
  visible: boolean;
};

export type OutLookAttachment = {
  '@odata.mediaContentType': string;
  '@odata.type': string;
  contentBytes: string;
  contentId: string;
  contentLocation: string;
  contentType: string;
  id: string;
  isInline: boolean;
  lastModifiedDateTime: string;
  name: string;
  size: number;
};

export type DocumentType = 'new' | 'meeting' | 'note';
export type DocumentTypeFilter = 'note,new' | 'meeting';
export const DocumentTypeFilterLabel: { [key in DocumentTypeFilter]: string } =
  {
    'note,new': '기본',
    meeting: '회의록',
  };

export type DocumentFilterPerson = {
  passportId: string;
  name: string;
  positionName?: string;
  divisionName?: string;
  departmentName?: string;
  profileImageUrl?: string;
};

export type TalkFlowFilterRecommendPerson = {
  passportId: string;
  name: string;
  nickName?: string;
  count: number;
  positionName?: string;
  divisionName?: string;
  departmentName?: string;
  profileImageUrl?: string;
};

export type DocumentListFilters = {
  category: string;
  keyword: string;
  listSize: number;
  currentPage: number;
  isOnlyShared: number;
  createdBy: DocumentFilterPerson[];
  internalParticipants: DocumentFilterPerson[];
  requireAllInternalParticipants: boolean;
  sortType: string;
  orderType: 'desc' | 'asc';
  searchFields: SearchFields;
  documentTypes: DocumentTypeFilter[];
  isRead: false | null;
  isBookmarked: boolean;
  id?: number | string;
  filterName?: string;
  createdStartedAt?: string;
  createdEndedAt?: string;
  meetingStartedAt?: string;
  meetingEndedAt?: string;
};

export type DocumentListParams = {
  keyword: string;
  listSize: number;
  currentPage: number;
  isOnlyShared: number;
  createdBy: string[];
  sortType: string;
  orderType: 'desc' | 'asc';
  searchFields: SearchFields;
  documentTypes: string;
  isRead: boolean | null;
  isBookmarked: boolean;
  createdStartedAt?: string;
  createdEndedAt?: string;
  meetingStartedAt?: string;
  meetingEndedAt?: string;
  internalParticipants?: string;
  requireAllInternalParticipants?: boolean;
};

export type LocalSale = {
  localSaleAmountDay: number;
  localSaleAmountExiDay: number;
  localSaleAmountBeforeDay: number;
  localSaleAmountExiBeforeDay: number;

  localSaleRatioDay: number;
  localSaleRatioExiDay: number;

  localSaleAmountWeek: number;
  localSaleAmountExiWeek: number;
  localSaleAmountBeforeWeek: number;
  localSaleAmountExiBeforeWeek: number;

  localSaleRatioWeek: number;
  localSaleRatioExiWeek: number;

  localSaleAmountMonth: number;
  localSaleAmountExiMonth: number;
  localSaleAmountBeforeMonth: number;
  localSaleAmountExiBeforeMonth: number;

  localSaleRatioMonth: number;
  localSaleRatioExiMonth: number;

  localSaleAmountYear: number;
  localSaleAmountExiYear: number;
  localSaleAmountBeforeYear: number;
  localSaleAmountExiBeforeYear: number;

  localSaleRatioYear: number;
  localSaleRatioExiYear: number;

  currencyUnit: Unit;
  channelType: 'ON' | 'OFF';
};

export type BrandSaleBase = {
  flagUrl: string;
  logoUrl: string;
  brandCode: string;
  brandName: string;
  season: string;
  sort: string;
  unLink: 'Y' | 'N';

  saleAmountDay: number;
  saleAmountExiDay: number;
  saleAmountBeforeDay: number;
  saleAmountExiBeforeDay: number;

  saleRatioDay: number;
  saleRatioExiDay: number;

  saleAmountWeek: number;
  saleAmountExiWeek: number;
  saleAmountBeforeWeek: number;
  saleAmountExiBeforeWeek: number;

  saleRatioWeek: number;
  saleRatioExiWeek: number;

  saleAmountMonth: number;
  saleAmountExiMonth: number;
  saleAmountBeforeMonth: number;
  saleAmountExiBeforeMonth: number;

  saleRatioMonth: number;
  saleRatioExiMonth: number;

  saleAmountYear: number;
  saleAmountExiYear: number;
  saleAmountBeforeYear: number;
  saleAmountExiBeforeYear: number;

  saleRatioYear: number;
  saleRatioExiYear: number;

  locals: LocalSale[];
  online: BrandSaleBase;
  offline: BrandSaleBase;
};

/** 시즌 구성 보기 — 이월 차수 구분 (PORTAL-14418). 응답 실측: Current | Previous | Next */
export type SeasonDivision = 'Current' | 'Previous' | 'Next';

/** 시즌별 뷰 한 줄 */
export type CategorySeasonWeight = {
  season: string;
  currentAmount: number;
  currentLocalAmount: number;
  /** 구성비(%) 문자열. 화면은 응답값을 그대로 쓰고 다시 계산하지 않는다 */
  saleWeight: string;
  localSaleWeight: string;
  currencyUnit: Unit;
};

/**
 * 이월 차수별 뷰 한 줄.
 * `currentSeason === 'ALL'` 이면 그 구분의 소계 행이다 — 구분마다 소계 1줄 + 시즌별 줄들이 함께 온다.
 */
export type CategorySeasonSummary = {
  seasonDivision: SeasonDivision;
  currentSeason: string;
  previousSeason: string;
  currentAmount: number;
  previousAmount: number;
  currentLocalAmount: number;
  previousLocalAmount: number;
  /** 전년비(%) 문자열. previousAmount 가 0 이면 비율이 성립하지 않아 "0.0" 이 온다 — 비교 불가로 가른다 */
  saleRatio: string;
  localSaleRatio: string;
  currencyUnit: Unit;
};

/** GET /api/v1/sales/brand/{brand}/product/subcategory/season (PORTAL-13974) */
export type CategorySeasonBreakdown = {
  seasonWeightList: CategorySeasonWeight[];
  seasonSummaryList: CategorySeasonSummary[];
};

export type CategorySeasonParams = {
  brand: string;
  date: string;
  period: Period;
  categorySubGroupCode: string;
  categoryItemCode: string;
};

export type SalesParams = {
  season: string;
  brand: string;
  date: string;
  period: string;
  shopCode?: string;
  /** 시즌 기준. 미지정·PRDT 면 쿼리에 붙이지 않는다 (PORTAL-14249) */
  seasonBasis?: SeasonBasis;
};

export type ShopGroup = {
  brandCode: string;
  season: string;
  period: string;
  shopTypeCode: string;
  shopGroupCode: FilterType;
  shopGroupName: string;
  currentAmount: number;
  previousAmount: number;
  saleRatio: string;
  saleWeight: string;
  operatingShopCnt: number;
  newShopCnt: number;
  closedShopCnt: number;
  shopList: ShopItem[];
  historyList: HistoryItem[];
  shopTypeName?: string;
  gradeName?: string;
  regionName?: string;
  channelName?: string;
  // 현지화 토글용(해외 브랜드) — BE가 추가한 현지매출 필드
  currentLocalAmount?: number;
  previousLocalAmount?: number;
  localSaleRatio?: string;
  currencyUnit?: Unit;
};

// 그룹별(채널/등급/지역) 매출 응답 — data 배열과 함께 불일치 플래그/차액을 최상위로 내려줌
export type ShopGroupResponse = {
  data: ShopGroup[];
  isMismatched: boolean;
  mismatchAmount: number;
};

export type ShopItem = {
  shopCode: string;
  shopName: string;
  currentAmount: number;
  previousAmount: number;
  saleRatio: string;
  closed: 'Y' | 'N';
  // 현지화 토글용(해외 브랜드)
  currentLocalAmount?: number;
  previousLocalAmount?: number;
  localSaleRatio?: string;
};

export type HistoryItem = {
  unit: string;
  currentAmount: number;
  previousAmount: number;
  // 현지화 토글용(해외 브랜드) — 차트 현지매출
  currentLocalAmount?: number;
  previousLocalAmount?: number;
};

export type BrandSummaryResponse = {
  achieveRate: string;
  brandCode: string;
  currentAmount: number;
  goalAmount: number;
  period: Period;
  historyList: HistoryItem[];
  previousAmount: number;
  saleRatio: string;
  season: string;
  achieveRateByMonth: number;
  goalAmountByMonth: number;
  dailyGoalRate: string;
  // 현지화 토글용(해외 브랜드) — BE가 추가한 현지매출 필드
  currentLocalAmount?: number;
  previousLocalAmount?: number;
  localSaleRatio?: string;
  currencyUnit?: Unit;
};

export type BrandMetaData = {
  brandCode: string;
  brandName: string;
  logoUrl: string;
  flagUrl: string;
};

export type FilterType = 'ALL' | 'EXI' | 'NEW';

export type ProductCategory = {
  brandCode: string;
  categoryGroupCode: string;
  categoryGroupName: string;
  currentAmount: number;
  period: string;
  previousAmount: number;
  saleRatio: string;
  saleWeight: string;
  season: string;
};

export type ProductSubCategory = {
  brandCode: string;
  categoryItemCode: string;
  categoryItemName: string;
  categorySubGroupCode: string;
  categorySubGroupName: string;
  currentAmount: number;
  historyList: HistoryItem[];
  period: string;
  prdtList: {
    productCode: string;
    currentQuantity: number;
    saleQuantityRatio: string;
    currentAmount: number;
    previousAmount: number;
    saleAmountRatio: string;
    image: string;
    previousQuantity: number;
    productName: string;
    // 현지화 토글용(해외 브랜드)
    currentLocalAmount?: number;
    previousLocalAmount?: number;
  }[];
  previousAmount: number;
  saleRatio: string;
  season: string;
  // 현지화 토글용(해외 브랜드) — BE가 추가한 현지매출 필드
  currentLocalAmount?: number;
  previousLocalAmount?: number;
  localSaleRatio?: string;
  currencyUnit?: Unit;
};

export type CategoryFilter = {
  brandCode: string;
  categoryList: {
    categoryGroupCode: string;
    categoryGroupName: string;
    categoryItemCode: string;
    categoryItemName: string;
    categorySubGroupCode: string;
    categorySubGroupName: string;
  }[];
};

export type PhoneBillResponse = {
  actualUsagePeriodMatch: boolean;
  aiReason: string;
  id: number;
  isFullSupport: boolean;
  nameNumberMatch: boolean;
  paymentAmountExists: boolean;
  reimbursementAmount: number;
  status: string;
};

export type PhoneBillStatus = {
  id: number;
  year: number;
  month: number;
  status: string;
  aiReason: string;
  isFullSupport: boolean;
  reimbursementAmount: number;
  submittedAt: string;
};
